/**
 * "My skill map" for the staff-facing performance page. Shows the latest
 * assessment the moment it is recorded (labelled with its sign-off stage),
 * the trend across past assessments, the assessor's remarks, and the full
 * criteria the person is measured against.
 */
import { useLang } from '../../../i18n/LangContext';
import React, { useState } from 'react';
import { Grid3x3, BadgeCheck, Loader2, ChevronDown, MessageSquare, TrendingUp } from 'lucide-react';
import { LevelChip, LEVEL_STYLE, indexTone } from '../skills/shared';

const STAGE = {
  proposed: { label: 'Awaiting supervisor sign-off', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  checked: { label: 'Checked by supervisor', cls: 'bg-sky-50 text-sky-800 border-sky-300' },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
};

const groupBy = (rows) => rows.reduce((acc, r) => {
  const g = r.group || 'Other';
  (acc[g] = acc[g] || []).push(r);
  return acc;
}, {});

const MySkillsCard = ({ data, onCertificate, busy, skillWeightPct }) => {
  const { t } = useLang();
  const [showCriteria, setShowCriteria] = useState(false);
  const latest = data?.latest;
  if (!latest) return null;

  const stage = STAGE[latest.status] || STAGE.proposed;
  const ratings = latest.ratings || {};
  const catalogue = data.catalogue || [];
  const levels = data.levels || [];
  const rated = catalogue.filter((sk) => ratings[sk.id]);
  const groups = groupBy(rated);
  const history = (data.history || []).filter((h) => h.skill_index !== null && h.skill_index !== undefined);
  const signed = data.snapshot || {};
  const canCertify = !!(signed.strengths || []).length;
  // Focus list comes from the sheet on screen, so it can never contradict the
  // ratings shown above it.
  const focus = rated
    .filter((sk) => ['not_ok', 'needs_improvement'].includes(ratings[sk.id]))
    .map((sk) => sk.name);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="my-skills-card">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <Grid3x3 size={15} className="text-teal-600" /> {t('My skill map')}
        </h2>
        <span className="text-xs text-slate-400">{t('assessed')} {latest.assessment_date}</span>
        <span
          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${stage.cls}`}
          data-testid="my-skills-stage"
        >
          {t(stage.label)}
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-5 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">{t('Skill index')}</div>
          <div className={`text-3xl font-semibold ${indexTone(latest.skill_index)}`} data-testid="my-skill-index">
            {latest.skill_index === null || latest.skill_index === undefined ? '—' : `${latest.skill_index}%`}
          </div>
        </div>
        {latest.delta !== null && latest.delta !== undefined && (
          <div className={`text-xs font-medium ${latest.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="my-skill-delta">
            {latest.delta >= 0 ? '+' : ''}{latest.delta} {t('vs previous assessment')}
          </div>
        )}
        {latest.status !== 'approved' && (
          <p className="text-xs text-slate-500 max-w-sm">
            {t('Your appraisal keeps using the last approved assessment until this one is signed off.')}
          </p>
        )}
      </div>

      {history.length > 1 && (
        <div className="mb-4" data-testid="my-skill-history">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
            <TrendingUp size={12} /> {t('My progress')}
          </div>
          <div className="flex items-end gap-3 overflow-x-auto pb-1">
            {history.map((h) => (
              <div key={h.assessment_date + h.skill_index} className="flex flex-col items-center gap-1 min-w-[52px]">
                <span className={`text-xs font-semibold ${indexTone(h.skill_index)}`}>{h.skill_index}%</span>
                <div
                  title={h.status === 'approved' ? 'Approved' : 'Not signed off yet'}
                  className={`w-8 rounded-t ${h.status === 'approved'
                    ? 'bg-teal-500/80' : 'border border-dashed border-teal-400 bg-teal-500/25'}`}
                  style={{ height: `${Math.max(6, (h.skill_index || 0) * 0.9)}px` }}
                />
                <span className="text-[10px] text-slate-400">{(h.assessment_date || '').slice(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {Object.entries(groups).map(([group, rows]) => (
          <div key={group} data-testid={`my-skill-group-${group.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{group}</div>
            <div className="grid gap-x-8 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {rows.map((sk) => (
                <div key={sk.id} className="flex w-full max-w-[320px] items-center justify-between gap-2 border-b border-slate-100 pb-1">
                  <span className="text-xs text-slate-700 truncate" title={sk.name}>{sk.name}</span>
                  <LevelChip level={ratings[sk.id]} size="sm" testid={`my-skill-${sk.id}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {(latest.notes || latest.training_needs || !!(latest.feedback_points || []).length) && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="my-skill-remarks">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
            <MessageSquare size={12} /> {t('Assessor remarks')}
          </div>
          {latest.notes && <p className="text-xs text-slate-700 whitespace-pre-line">{latest.notes}</p>}
          {!!(latest.feedback_points || []).length && (
            <ul className="mt-1.5 space-y-1">
              {latest.feedback_points.map((p) => (
                <li key={p} className="flex gap-1.5 text-xs text-slate-700">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />{p}
                </li>
              ))}
            </ul>
          )}
          {latest.training_needs && (
            <p className="mt-1.5 text-xs text-slate-700">
              <span className="font-medium text-slate-600">{t('Training needs')}: </span>{latest.training_needs}
            </p>
          )}
        </div>
      )}

      {!!focus.length && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-3" data-testid="my-skill-focus">
          <span className="font-medium">{t('Focus next')}: </span>{focus.join(', ')}
        </p>
      )}

      {canCertify && (
        <button
          onClick={onCertificate} disabled={busy === 'cert'}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm hover:bg-amber-100 disabled:opacity-50"
          data-testid="my-certificate-btn"
        >
          {busy === 'cert' ? <Loader2 size={15} className="animate-spin" /> : <BadgeCheck size={15} />}
          {t('My skill certificate')}
        </button>
      )}

      {/* ---- criteria ---- */}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <button
          onClick={() => setShowCriteria((v) => !v)}
          className="flex w-full items-center gap-1.5 text-left text-xs font-semibold text-slate-700"
          data-testid="my-skill-criteria-toggle"
          aria-expanded={showCriteria}
        >
          {t('How my skills are assessed')}
          <span className="font-normal text-slate-400">
            — {t('the rating scale and every skill I can be measured on')}
          </span>
          <ChevronDown size={14} className={`ml-auto shrink-0 text-slate-400 transition-transform ${showCriteria ? 'rotate-180' : ''}`} />
        </button>

        {showCriteria && (
          <div className="mt-3 space-y-3" data-testid="my-skill-criteria">
            <div className="grid gap-1.5 sm:grid-cols-2">
              {levels.map((l) => (
                <div key={l.key} className="flex items-center gap-2">
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md border text-[11px] font-semibold ${LEVEL_STYLE[l.key]}`}>
                    {l.symbol}
                  </span>
                  <span className="text-xs text-slate-700">{l.label}</span>
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-600">
              Each skill is rated on that scale and averaged into a single skill index.
              {skillWeightPct ? ` It carries ${skillWeightPct}% of your overall appraisal score` : ' It feeds your overall appraisal score'}
              , and any skill marked <b>Not OK</b> or <b>Needs improvement</b> becomes a training task with a trainer.
              An assessment counts towards your appraisal only once your supervisor and management have signed it off.
            </p>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                Skills for my role{data.role ? ` (${data.role})` : ''}
              </div>
              <div className="space-y-2">
                {Object.entries(groupBy(catalogue)).map(([group, rows]) => (
                  <div key={group} className="rounded-lg border border-slate-200 p-2.5">
                    <div className="text-xs font-semibold text-slate-800 mb-1">{group}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {rows.map((sk) => (
                        <span
                          key={sk.id}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600"
                        >
                          {sk.name}
                          {ratings[sk.id] && <LevelChip level={ratings[sk.id]} size="sm" />}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySkillsCard;
